/**
 * Validate email format
 * @param email - The email to validate
 * @returns Boolean indicating if the email is valid
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate password strength
 * @param password - The password to validate
 * @returns Object with validation result and message
 */
export const validatePassword = (
  password: string
): { isValid: boolean; message: string } => {
  if (password.length < 8) {
    return {
      isValid: false,
      message: 'Password must be at least 8 characters long',
    };
  }

  if (!/[A-Z]/.test(password)) {
    return {
      isValid: false,
      message: 'Password must contain at least one uppercase letter',
    };
  }

  if (!/[a-z]/.test(password)) {
    return {
      isValid: false,
      message: 'Password must contain at least one lowercase letter',
    };
  }

  if (!/[0-9]/.test(password)) {
    return {
      isValid: false,
      message: 'Password must contain at least one number',
    };
  }

  return {
    isValid: true,
    message: 'Password is strong',
  };
};

/**
 * Validate required field
 * @param value - The value to validate
 * @returns Boolean indicating if the field is not empty
 */
export const isRequired = (value: string | number | null | undefined): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

/**
 * Validate number range
 * @param value - The number to validate
 * @param min - Minimum allowed value
 * @param max - Maximum allowed value
 * @returns Boolean indicating if the number is within range
 */
export const isInRange = (
  value: number,
  min: number,
  max: number
): boolean => {
  return value >= min && value <= max;
};

/**
 * Validate credit card number format
 * @param cardNumber - The card number to validate
 * @returns Boolean indicating if the card number format is valid
 */
export const isValidCreditCardNumber = (cardNumber: string): boolean => {
  // Remove spaces and dashes
  const sanitized = cardNumber.replace(/[\s-]/g, '');
  
  // Check if it contains only digits and has valid length
  return /^\d{13,19}$/.test(sanitized);
};

/**
 * Validate phone number format
 * @param phoneNumber - The phone number to validate
 * @returns Boolean indicating if the phone number format is valid
 */
export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  // Basic validation for international phone numbers
  // Allows for country code, spaces, dashes, and parentheses
  const phoneRegex = /^\+?[0-9\s\-()]{8,20}$/;
  return phoneRegex.test(phoneNumber);
};

/**
 * Parse phone number to extract country code
 * @param phoneNumber - The phone number to parse
 * @returns Object with country code and national number
 */
export const parsePhoneNumber = (phoneNumber: string): { countryCode: string, nationalNumber: string } => {
  // Default to UK
  let countryCode = '+44';
  let nationalNumber = phoneNumber;
  
  // Common country codes to check for
  const commonCodes = ['+44', '+1', '+33', '+49', '+61', '+86', '+91', '+81', '+7', '+55'];
  
  // Check if the phone number starts with a plus sign
  if (phoneNumber.startsWith('+')) {
    // Try to match with common country codes first
    for (const code of commonCodes) {
      if (phoneNumber.startsWith(code)) {
        countryCode = code;
        nationalNumber = phoneNumber.substring(code.length).trim();
        return { countryCode, nationalNumber };
      }
    }
    
    // If no common code matches, extract using regex
    const match = phoneNumber.match(/^\+(\d{1,4})\s*(.*)$/);
    if (match) {
      countryCode = `+${match[1]}`;
      nationalNumber = match[2];
    }
  }
  
  return { countryCode, nationalNumber };
};

/**
 * Validate form fields
 * @param fields - Object containing field values
 * @param validations - Object containing validation rules for each field
 * @returns Object containing validation errors
 */
export const validateForm = (
  fields: Record<string, any>,
  validations: Record<string, (value: any) => boolean>
): Record<string, string> => {
  const errors: Record<string, string> = {};
  
  Object.keys(validations).forEach(field => {
    const value = fields[field];
    const validation = validations[field];
    
    if (!validation(value)) {
      errors[field] = `${field} is invalid`;
    }
  });
  
  return errors;
};
